import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  Clock,
  Crown,
  Star,
  User,
  RefreshCw,
  Bell,
  CheckCircle2,
  XCircle,
  Phone,
  Mail,
  Calendar,
  TrendingUp,
  Users,
  DollarSign,
  Filter
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import DashboardLayout from "../../components/DashboardLayout";
import { ServiceLevelBadge } from "../../components/ServiceLevelBadge";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const SLADashboard = () => {
  const [alerts, setAlerts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterPlan, setFilterPlan] = useState("all");
  const [filterSeverity, setFilterSeverity] = useState("all");

  useEffect(() => {
    fetchData();
    // Auto-refresh every 60 seconds
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    try {
      const [alertsRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/sla/alerts`),
        axios.get(`${API}/admin/reports/by-plan`)
      ]);
      setAlerts(alertsRes.data.alerts || []);
      setStats(statsRes.data);
    } catch (error) {
      console.error("Error fetching SLA data:", error);
      toast.error("Error al cargar datos de SLA");
    } finally {
      setLoading(false);
    }
  };

  const filteredAlerts = alerts.filter(alert => {
    if (filterPlan !== "all" && alert.plan_type !== filterPlan) return false;
    if (filterSeverity !== "all" && alert.severity !== filterSeverity) return false;
    return true;
  });

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case "high": return <Crown className="w-5 h-5 text-amber-500" />;
      case "medium": return <Star className="w-5 h-5 text-blue-500" />;
      default: return <User className="w-5 h-5 text-gray-500" />;
    }
  };

  const getSeverityBg = (severity) => {
    switch (severity) {
      case "high": return "bg-red-50 border-red-200";
      case "medium": return "bg-amber-50 border-amber-200";
      default: return "bg-gray-50 border-gray-200";
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-emerald-600 border-t-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  const vipAlerts = alerts.filter(a => a.plan_type === "vip").length;
  const premiumAlerts = alerts.filter(a => a.plan_type === "premium").length;
  const standardAlerts = alerts.filter(a => a.plan_type === "estandar").length;

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto" data-testid="sla-dashboard">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-heading text-3xl font-bold text-navy-900">
              Dashboard de SLA
            </h1>
            <p className="text-navy-500">Monitoreo de tiempos de respuesta por nivel de servicio</p>
          </div>
          <Button onClick={fetchData} variant="outline">
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualizar
          </Button>
        </div>

        {/* Alert Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          {/* VIP Alerts */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className={`p-5 rounded-xl border-2 ${vipAlerts > 0 ? "bg-red-50 border-red-300" : "bg-amber-50 border-amber-200"}`}
          >
            <div className="flex items-center justify-between mb-2">
              <Crown className="w-8 h-8 text-amber-500" />
              {vipAlerts > 0 && <span className="animate-pulse w-3 h-3 bg-red-500 rounded-full"></span>}
            </div>
            <p className="text-3xl font-bold text-navy-900">{vipAlerts}</p>
            <p className="text-sm text-navy-600">Alertas VIP</p>
            {vipAlerts > 0 && (
              <p className="text-xs text-red-600 mt-1 font-medium">¡Requiere atención inmediata!</p>
            )}
          </motion.div>

          {/* Premium Alerts */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className={`p-5 rounded-xl border-2 ${premiumAlerts > 0 ? "bg-amber-50 border-amber-300" : "bg-blue-50 border-blue-200"}`}
          >
            <div className="flex items-center justify-between mb-2">
              <Star className="w-8 h-8 text-blue-500" />
              {premiumAlerts > 0 && <span className="w-3 h-3 bg-amber-500 rounded-full"></span>}
            </div>
            <p className="text-3xl font-bold text-navy-900">{premiumAlerts}</p>
            <p className="text-sm text-navy-600">Alertas Premium</p>
            {premiumAlerts > 0 && (
              <p className="text-xs text-amber-600 mt-1 font-medium">Atención prioritaria</p>
            )}
          </motion.div>

          {/* Standard Alerts */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="p-5 rounded-xl border-2 bg-gray-50 border-gray-200"
          >
            <div className="flex items-center justify-between mb-2">
              <User className="w-8 h-8 text-gray-500" />
            </div>
            <p className="text-3xl font-bold text-navy-900">{standardAlerts}</p>
            <p className="text-sm text-navy-600">Alertas Estándar</p>
          </motion.div>

          {/* Total Active */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="p-5 rounded-xl border-2 bg-emerald-50 border-emerald-200"
          >
            <div className="flex items-center justify-between mb-2">
              <CheckCircle2 className="w-8 h-8 text-emerald-500" />
            </div>
            <p className="text-3xl font-bold text-navy-900">
              {(stats?.stats?.vip?.count || 0) + (stats?.stats?.premium?.count || 0) + (stats?.stats?.estandar?.count || 0) - alerts.length}
            </p>
            <p className="text-sm text-navy-600">Clientes al día</p>
          </motion.div>
        </div>

        {/* Stats by Plan */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            {["vip", "premium", "estandar"].map((planKey) => {
              const planStats = stats.stats[planKey] || {};
              const planInfo = stats.plan_info[planKey] || {};
              return (
                <motion.div
                  key={planKey}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className={`p-5 rounded-xl border ${
                    planKey === "vip" ? "bg-amber-50 border-amber-200" :
                    planKey === "premium" ? "bg-blue-50 border-blue-200" :
                    "bg-gray-50 border-gray-200"
                  }`}
                >
                  <div className="flex items-center justify-between mb-4">
                    <ServiceLevelBadge planType={planKey} showPrice />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-2xl font-bold text-navy-900">{planStats.count || 0}</p>
                      <p className="text-xs text-navy-500">Total clientes</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-navy-900">{planStats.active || 0}</p>
                      <p className="text-xs text-navy-500">Activos</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-emerald-600">{planStats.completed || 0}</p>
                      <p className="text-xs text-navy-500">Completados</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-navy-900">${(planStats.revenue || 0).toFixed(2)}</p>
                      <p className="text-xs text-navy-500">Ingresos</p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Alerts Section */}
        <div className="bg-white rounded-2xl border border-navy-100 overflow-hidden">
          <div className="p-5 border-b border-navy-100 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-6 h-6 text-amber-500" />
              <div>
                <h2 className="font-semibold text-navy-900">Alertas de SLA</h2>
                <p className="text-sm text-navy-500">Clientes que requieren atención según su nivel de servicio</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Select value={filterPlan} onValueChange={setFilterPlan}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Filtrar plan" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los planes</SelectItem>
                  <SelectItem value="vip">VIP</SelectItem>
                  <SelectItem value="premium">Premium</SelectItem>
                  <SelectItem value="estandar">Estándar</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filterSeverity} onValueChange={setFilterSeverity}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Severidad" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  <SelectItem value="high">Alta (VIP)</SelectItem>
                  <SelectItem value="medium">Media (Premium)</SelectItem>
                  <SelectItem value="low">Baja (Estándar)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {filteredAlerts.length === 0 ? (
            <div className="p-12 text-center">
              <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-navy-900 mb-2">¡Todo al día!</h3>
              <p className="text-navy-500">No hay clientes que requieran atención urgente.</p>
            </div>
          ) : (
            <div className="divide-y divide-navy-100">
              {filteredAlerts.map((alert, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: idx * 0.05 }}
                  className={`p-5 ${getSeverityBg(alert.severity)} hover:bg-opacity-75 transition-colors`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <div className={`p-2 rounded-full ${
                        alert.severity === "high" ? "bg-red-100" :
                        alert.severity === "medium" ? "bg-amber-100" : "bg-gray-100"
                      }`}>
                        {getSeverityIcon(alert.severity)}
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-navy-900">{alert.client_name}</h3>
                          <ServiceLevelBadge planType={alert.plan_type} size="sm" />
                        </div>
                        <p className="text-sm text-navy-600 mb-2">{alert.client_email}</p>
                        <div className="flex items-center gap-4 text-sm">
                          <span className="flex items-center gap-1 text-red-600">
                            <Clock className="w-4 h-4" />
                            {alert.days_without_contact} días sin contacto
                          </span>
                          <span className="text-navy-500">
                            (máximo permitido: {alert.max_allowed_days} días)
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Link to={`/admin/cases/${alert.case_id}`}>
                        <Button size="sm" variant="outline">
                          Ver caso
                        </Button>
                      </Link>
                      {alert.severity === "high" && (
                        <Button size="sm" className="bg-red-600 hover:bg-red-700">
                          <Bell className="w-4 h-4 mr-1" />
                          Urgente
                        </Button>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>

        {/* SLA Reference Table */}
        <div className="mt-6 bg-white rounded-2xl border border-navy-100 overflow-hidden">
          <div className="p-5 border-b border-navy-100">
            <h2 className="font-semibold text-navy-900">Referencia de SLA por Nivel</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-navy-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-navy-600 uppercase">Nivel</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-navy-600 uppercase">Tiempo Respuesta</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-navy-600 uppercase">Proceso</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-navy-600 uppercase">Máx. sin contacto</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-navy-600 uppercase">Recordatorios</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-navy-100">
                <tr className="hover:bg-amber-50">
                  <td className="px-6 py-4"><ServiceLevelBadge planType="vip" showPrice /></td>
                  <td className="px-6 py-4 text-sm text-navy-700">Máximo 24 horas</td>
                  <td className="px-6 py-4 text-sm text-navy-700">Acelerado (~3 semanas)</td>
                  <td className="px-6 py-4 text-sm text-red-600 font-medium">1 día</td>
                  <td className="px-6 py-4 text-sm text-navy-700">Diarios</td>
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="px-6 py-4"><ServiceLevelBadge planType="premium" showPrice /></td>
                  <td className="px-6 py-4 text-sm text-navy-700">2 a 4 días</td>
                  <td className="px-6 py-4 text-sm text-navy-700">4-5 semanas</td>
                  <td className="px-6 py-4 text-sm text-amber-600 font-medium">4 días</td>
                  <td className="px-6 py-4 text-sm text-navy-700">Cada 3 días</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4"><ServiceLevelBadge planType="estandar" showPrice /></td>
                  <td className="px-6 py-4 text-sm text-navy-700">3 a 7 días</td>
                  <td className="px-6 py-4 text-sm text-navy-700">Hasta 9 semanas</td>
                  <td className="px-6 py-4 text-sm text-navy-600">7 días</td>
                  <td className="px-6 py-4 text-sm text-navy-700">Cada 5 días</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default SLADashboard;
